import axios from 'axios'

// API基础配置 - 开发环境使用相对路径，走 vite/webpack 代理
const API_BASE = process.env.VUE_APP_API_URL || '/api'

const apiClient = axios.create({
  baseURL: API_BASE,
  timeout: 120000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// 请求拦截器
apiClient.interceptors.request.use(
  config => {
    console.log('[API] ' + (config.method ? config.method.toUpperCase() : '') + ' ' + config.url)
    return config
  },
  error => {
    console.error('[API] Request Error:', error)
    return Promise.reject(error)
  }
)

// 响应拦截器
apiClient.interceptors.response.use(
  response => response.data,
  error => {
    const message = (error.response && error.response.data && error.response.data.detail) || error.message || '请求失败'
    console.error('[API] Response Error:', message)
    return Promise.reject(error)
  }
)

// ============ 第一本账 API ============
export const firstBookApi = {
  // 获取当前第一本账（实时行情）
  getCurrent: () => apiClient.get('/first-book/current'),

  // 获取历史日线第一本账
  getHistory: (tradeDate) => apiClient.get('/first-book/history', { params: { trade_date: tradeDate } }),
  
  // 生成变更diff
  generateDiff: (data) => apiClient.post('/first-book/diff', data),
  
  // 确认变更写入
  confirmChange: (diffId) => apiClient.post('/first-book/confirm', { diff_id: diffId, confirmed: true }),
  
  // 批量清空预览（高风险）
  generateClearPreview: (zone = 'formal') => apiClient.post('/first-book/clear-preview', { zone }),
  
  // 批量清空执行（高风险）
  executeClear: (diffIds, confirmToken) => apiClient.post('/first-book/clear-execute', { diff_ids: diffIds, confirm_token: confirmToken }),
  
  // 读取正式区
  getFormal: () => apiClient.get('/first-book/formal'),
  
  // 读取临时区
  getTemp: () => apiClient.get('/first-book/temp'),
  
  // 添加股票到正式区
  addToFormal: (data) => apiClient.post('/first-book/formal/add', data),
  
  // 添加股票到临时区
  addToTemp: (data) => apiClient.post('/first-book/temp/add', data),
  
  // 删除股票
  deleteStock: (tsCode) => apiClient.delete(`/first-book/stock/${tsCode}`),
  
  // 更新标签
  updateTag: (tsCode, data) => apiClient.put(`/first-book/stock/${tsCode}/tag`, data),

  // 个股新闻 - 查询单只股票新闻
  getStockNews: (tsCode, params) => apiClient.get(`/first-book/news/${tsCode}`, { params }),

  // 个股新闻 - 批量查询自选池新闻
  batchGetStockNews: (params) => apiClient.get('/first-book/news/batch', { params }),
  
  // 股票代码/名称联想搜索
  getSuggestions: (keyword) => apiClient.get('/first-book/suggestions', { params: { keyword, limit: 20 } }),
  
  // 获取热门股票列表（不依赖Tushare）
  getPopularStocks: (keyword = '') => apiClient.get('/first-book/popular-stocks', { params: { keyword, limit: 50 } }),
  
  // 根据代码查找名称
  resolveName: (tsCode) => apiClient.get(`/first-book/resolve-name/${tsCode}`)
}



// ============ 股票搜索 API ============
export const stockApi = {
  // 股票代码模糊搜索（全量A股，从Tushare stock_basic加载）
  getSuggestions: (keyword, limit = 15) => apiClient.get('/stock/suggestions', { params: { q: keyword, limit } })
}

// ============ 互动易问答 API ============
export const irmQaApi = {
  // 查询互动易问答（默认第一本账正式区全部股票）
  query: (params) => apiClient.get('/stock/irm-qa', { params })
}

// ============ 技术指标 API ============
export const indicatorApi = {
  // 获取某股票指标
  getByTsCode: (tsCode, params) => apiClient.get(`/indicator/${tsCode}`, { params }),
  
  // 计算指标
  calculate: (data) => apiClient.post('/indicator/calculate', data),

  // 批量获取第一本账正式区全部股票技术指标
  getBatchFirstBook: (params) => apiClient.get('/indicator/batch-first-book', { params }),

  // 手动触发分钟K线采集
  collectMinutes: (params) => apiClient.post('/indicator/collect-minutes', null, { params }),

  // 查询已存储的分钟K线数据
  getStoredMinutes: (tsCode, params) => apiClient.get(`/indicator/stored-minutes/${tsCode}`, { params }),
}

// ============ 指数技术指标 API ============
export const indexIndicatorApi = {
  // 获取某指数指标
  getByTsCode: (tsCode, params) => apiClient.get(`/index-indicator/${tsCode}`, { params }),

  // 批量获取核心指数技术指标
  getBatch: (params) => apiClient.get('/index-indicator/batch', { params }),

  // 获取核心指数列表
  getHotIndexes: () => apiClient.get('/index-indicator/hot-indexes')
}

// ============ 数据源 API ============
export const dataSourceApi = {
  // 自检数据源
  check: () => apiClient.get('/data-source/check'),
  
  // 获取Tushare token状态
  getTokenStatus: () => apiClient.get('/data-source/tushare/status'),
  
  // 测试Tushare接口
  testInterface: (interfaceName) => apiClient.post('/data-source/tushare/test', { interface: interfaceName }),
  
  // 获取K线数据（支持日线/周线/月线）- 旧接口
  getKlineData: (tsCode, params) => apiClient.get(`/stock/kline/${tsCode}`, { params }),
  // 增强版K线数据（支持分钟级+预计算指标）- 新接口
  getKlineDataV2: (tsCode, params) => apiClient.get('/kline/data', { params: { ts_code: tsCode, ...params } }),
  // 获取实时行情快照
  getRealtimeQuote: (tsCode) => apiClient.get('/kline/realtime', { params: { ts_code: tsCode } }),
  // 获取综合实时详情(买卖盘口+逐笔成交+资金流向)
  getRealtimeDetail: (tsCode) => apiClient.get('/kline/detail', { params: { ts_code: tsCode } }),
  // 获取集合竞价数据(开盘+收盘)
  getAuction: (tsCode, tradeDate = null) => apiClient.get('/kline/auction', { params: { ts_code: tsCode, trade_date: tradeDate } }),
  // 获取技术面因子专业版数据(stk_factor_pro)
  getFactorPro: (tsCode, params = {}) => apiClient.get('/kline/factor-pro', { params: { ts_code: tsCode, ...params } }),
  // 批量获取第一本账正式区全部股票技术面因子
  getFactorProBatch: (params = {}) => apiClient.get('/kline/factor-pro-batch', { params })
}

// ============ 通知 API ============
export const notificationApi = {
  // 获取通知历史
  getHistory: (params) => apiClient.get('/notification/logs', { params }),
  
  // 发送微信测试（企业微信webhook）
  testWechat: (data) => apiClient.post('/notification/wechat/test', data),
  
  // 发送微信模板消息测试（公众号模板消息）
  testTemplate: (data) => apiClient.post('/notification/template/test', data),
  
  // 发送邮件测试
  testEmail: (data) => apiClient.post('/notification/email/test', data),
  
  // 发送Server酱测试
  testServerchan: (data) => apiClient.post('/notification/serverchan/test', data),
  
  // 获取正式区公告（新增）
  getFormalAnnouncements: (params) => apiClient.get('/notification/formal-announcements', { params }),

  // 查询单只股票公告
  getSingleAnnouncements: (tsCode, params) => apiClient.get(`/notification/announcements/${tsCode}`, { params }),

  // 公告事件 - 回购
  getRepurchase: (tsCode, params) => apiClient.get(`/notification/repurchase/${tsCode}`, { params }),
  // 公告事件 - 分红
  getDividend: (tsCode, params) => apiClient.get(`/notification/dividend/${tsCode}`, { params }),
  // 公告事件 - 解禁
  getShareFloat: (tsCode, params) => apiClient.get(`/notification/share-float/${tsCode}`, { params }),
  // 公告事件 - 股东增减持
  getHoldertrade: (tsCode, params) => apiClient.get(`/notification/holdertrade/${tsCode}`, { params })
}

// ============ 日志 API ============
export const logApi = {
  // 按log_id查询
  getById: (logId) => apiClient.get(`/log/${logId}`),
  
  // 按request_id查询
  getByRequestId: (requestId) => apiClient.get(`/log/request/${requestId}`),
  
  // 按task_id查询任务日志
  getByTaskId: (taskId) => apiClient.get(`/log/task/${taskId}`),

  // 获取所有日志列表
  listLogs: (params) => apiClient.get('/log/list', { params })
}

// ============ 分析结论 API ============
export const conclusionApi = {
  // 保存结论
  save: (data) => apiClient.post('/analysis-conclusion/save', data),

  // 获取结论列表
  getList: (params) => apiClient.get('/analysis-conclusion/list', { params }),

  // 获取结论详情
  getDetail: (conclusionId) => apiClient.get(`/analysis-conclusion/detail/${conclusionId}`),

  // 获取正式分析只读 Receipt（summary / advanced）
  getReceipt: (conclusionId, view = 'summary') => apiClient.get(`/analysis-conclusion/receipt/${encodeURIComponent(conclusionId)}`, {
    params: { view }
  }),

  // 按股票代码获取最近N次结论
  getByTsCode: (tsCode, limit = 10) => apiClient.get(`/analysis-conclusion/${tsCode}`, {
    params: { limit }
  }),

  // 更新验证结果
  updateVerification: (conclusionId, data) => apiClient.patch(`/analysis-conclusion/${conclusionId}/verify`, data),

  // 删除结论
  delete: (conclusionId) => apiClient.delete(`/analysis-conclusion/${conclusionId}`)
}

// ============ 定时任务 API ============
export const scheduledTaskApi = {
  // 获取任务列表
  getTasks: (params) => apiClient.get('/scheduled-tasks/', { params }),

  // 获取单个任务
  getTask: (taskId) => apiClient.get(`/scheduled-tasks/${taskId}`),

  // 创建任务（JSON body，修复数组序列化问题）
  createTask: (data) => apiClient.post('/scheduled-tasks/', data),

  // 更新任务（JSON body，修复数组序列化问题）
  updateTask: (taskId, data) => apiClient.put(`/scheduled-tasks/${taskId}`, data),

  // 删除任务
  deleteTask: (taskId) => apiClient.delete(`/scheduled-tasks/${taskId}`),

  // 暂停任务
  pauseTask: (taskId) => apiClient.post(`/scheduled-tasks/${taskId}/pause`),

  // 恢复任务
  resumeTask: (taskId) => apiClient.post(`/scheduled-tasks/${taskId}/resume`),

  // 立即执行任务
  runTask: (taskId) => apiClient.post(`/scheduled-tasks/${taskId}/run`),

  // 获取运行日志列表
  getRuns: (params) => apiClient.get('/scheduled-tasks/runs/', { params }),

  // 获取单条运行记录
  getRun: (runId) => apiClient.get(`/scheduled-tasks/runs/${runId}`),

  // 获取监控日志列表
  getMonitorLogs: (params) => apiClient.get('/scheduled-tasks/monitor-logs/', { params }),

  // 系统自检
  healthCheck: () => apiClient.get('/scheduled-tasks/health-check')
}

// ============ 首页总览 Dashboard API ============
export const dashboardApi = {
  // 获取首页总览数据
  getDashboard: () => apiClient.get('/dashboard/'),

  // 获取第一本账预览
  getFirstBookPreview: () => apiClient.get('/dashboard/first-book-preview'),

  // 获取任务预览
  getTaskPreview: () => apiClient.get('/dashboard/task-preview')
}

// ============ 聊天触发分析 API ============
export const chatAnalysisApi = {
  // 创建分析请求
  createRequest: (params) => apiClient.post('/analysis/request', null, { params }),

  // 保存/生成分析载荷
  savePayload: (params) => apiClient.post('/analysis/payload', null, { params }),

  // 按请求ID获取载荷
  getPayload: (requestId) => apiClient.get(`/analysis/payload/${requestId}`),

  // 保存分析结论
  saveConclusion: (params) => apiClient.post('/analysis/conclusion/save', null, { params }),

  // 查询分析请求状态
  getRequestStatus: (requestId) => apiClient.get(`/analysis/request/${requestId}/status`),

  // 查询分析历史
  getHistory: (params) => apiClient.get('/analysis/history', { params }),

  // 按批次ID拉取详情
  getBatchPayload: (batchLogId) => apiClient.get(`/analysis/batch/${batchLogId}`),

  // 查询最近结论
  getRecentConclusions: (params) => apiClient.get('/analysis/conclusions', { params }),

  // 全链路查询
  getFullChain: (requestId) => apiClient.get(`/analysis/chain/${requestId}`),

  // 记录工具调用
  logToolCall: (params) => apiClient.post('/analysis/tool-call/log', null, { params }),

  // 查询工具调用日志
  getToolCallLogs: (params) => apiClient.get('/analysis/tool-call/logs', { params }),

  // 获取股票分析数据（旧接口）
  getStockData: (data) => apiClient.post('/chat-analysis/stock-data', data),

  // 记录聊天触发（旧接口）
  logTrigger: (params) => apiClient.post('/chat-analysis/trigger-log', null, { params }),

  // 获取触发类型列表（旧接口）
  getTriggerTypes: () => apiClient.get('/chat-analysis/trigger-types'),

  // 一键股票综合分析（输入名称/代码 → 聚合所有数据 → AI分析 → 返回结论）
  autoAnalyze: (params) => apiClient.post('/analysis/auto', null, { params }),

  // 预览股票数据（仅聚合数据，不调用AI）
  previewData: (params) => apiClient.post('/analysis/preview', null, { params })
}

// ============ 资料与规则 API ============
export const documentsApi = {
  // 获取文档列表
  getList: (params) => apiClient.get('/documents/', { params }),

  // 获取文档详情
  getDetail: (docId) => apiClient.get(`/documents/${docId}`),

  // 创建文档
  create: (data) => apiClient.post('/documents/', data),

  // 更新文档
  update: (docId, data) => apiClient.put(`/documents/${docId}`, data),

  // 废弃文档
  deprecate: (docId, reason) => apiClient.post(`/documents/${docId}/deprecate`, { reason }),

  // 获取规则版本
  getRuleVersions: () => apiClient.get('/documents/rules/versions'),

  // 上传文档
  upload: (formData) => apiClient.post('/documents/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),

  // 验证文档
  validate: () => apiClient.get('/documents/validation/check')
}

// ============ 股票分钟行情 API ============
export const stkMinsApi = {
  // 查询分钟数据
  query: (params) => apiClient.get('/stk-mins/query', { params }),

  // 获取支持的频度
  getFreqs: () => apiClient.get('/stk-mins/available-freqs'),

  // 快速查询最近N天
  quickQuery: (tsCode, freq, days = 5) => apiClient.get(`/stk-mins/quick/${tsCode}`, { params: { freq, days } })
}

// ============ 实时指数分钟行情 API ============
export const rtIdxMinApi = {
  // 查询实时分钟数据（支持多代码）
  query: (params) => apiClient.get('/rt-idx-min/query', { params }),

  // 查询日内全量分钟数据（仅单代码）
  queryDaily: (params) => apiClient.get('/rt-idx-min/daily', { params }),

  // 获取支持的频度
  getFreqs: () => apiClient.get('/rt-idx-min/available-freqs'),

  // 获取热门指数
  getHotIndexes: () => apiClient.get('/rt-idx-min/hot-indexes')
}

// ============ 指数历史分钟行情 API ============
export const idxMinsApi = {
  // 查询历史分钟数据
  query: (params) => apiClient.get('/idx-mins/query', { params }),

  // 快速查询最近N天
  quickQuery: (tsCode, freq, days = 5) => apiClient.get(`/idx-mins/quick/${tsCode}`, { params: { freq, days } }),

  // 获取支持的频度
  getFreqs: () => apiClient.get('/idx-mins/available-freqs'),

  // 获取热门指数
  getHotIndexes: () => apiClient.get('/idx-mins/hot-indexes')
}

// ============ 系统设置 API ============
export const systemSettingsApi = {
  // 获取MCP工具列表
  getMcpTools: () => apiClient.get('/system-settings/mcp/tools'),

  // 获取单个MCP工具
  getMcpTool: (toolName) => apiClient.get(`/system-settings/mcp/tools/${toolName}`),

  // 获取权限级别
  getPermissionLevels: () => apiClient.get('/system-settings/mcp/permissions'),

  // 测试MCP工具
  testMcpTool: (toolName, params) => apiClient.post(`/system-settings/mcp/test/${toolName}`, params),

  // 获取系统配置
  getConfig: () => apiClient.get('/system-settings/config'),

  // 更新通知配置
  updateNotificationConfig: (data) => apiClient.put('/system-settings/config/notification', data),

  // 更新数据源配置
  updateDataSourceConfig: (data) => apiClient.put('/system-settings/config/data-source', data),

  // 启用/禁用MCP
  toggleMcp: (enabled) => apiClient.post('/system-settings/config/mcp/enable', null, { params: { enabled } }),

  // 获取系统信息
  getSystemInfo: () => apiClient.get('/system-settings/info'),

  // 系统健康检查
  healthCheck: () => apiClient.get('/system-settings/health-check'),

  // 模型管理
  getModelList: () => apiClient.get('/system-settings/model/list'),
  getCurrentModel: () => apiClient.get('/system-settings/model/current'),
  switchModel: (modelId) => apiClient.post('/system-settings/model/switch', { model_id: modelId }),
  testModel: (modelId) => apiClient.post('/system-settings/model/test', null, { params: { model_id: modelId } })
}

// ============ 市场数据 API (v2新增) ============
export const marketDataApi = {
  // 核心指数实时行情
  getIndexQuotes: () => apiClient.get('/market/index-quotes'),
  // 指数K线
  getIndexKline: (indexCode, params) => apiClient.get('/market/index-kline', { params: { index_code: indexCode, ...params } }),
  // 龙虎榜
  getLhbData: (params) => apiClient.get('/market/lhb', { params }),
  // ETF排行（旧版同花顺数据源）
  getEtfRanking: (params) => apiClient.get('/market/etf', { params }),
  // ETF基础信息 (Tushare etf_basic)
  getEtfBasic: (params) => apiClient.get('/etf/basic', { params }),
  // ETF行情数据 (Tushare fund_daily) - 涨跌幅+成交额
  getEtfDaily: (params) => apiClient.get('/etf/daily', { params }),
  // ETF分钟数据 (Tushare rt_min)
  getEtfMinutes: (params) => apiClient.get('/etf/minutes', { params }),
  // ETF成交额排行 (Tushare fund_daily)
  getEtfRankingV2: (params) => apiClient.get('/etf/ranking', { params }),
  // ETF基准指数列表
  getEtfIndexes: (params) => apiClient.get('/etf/indexes', { params }),
  // ETF与第一本账映射
  getEtfFirstBookMapping: (params) => apiClient.get('/etf/first-book-mapping', { params }),
  // 深市ETF实时参考
  getEtfIopv: (params) => apiClient.get('/etf/iopv', { params }),
  // 北上资金(沪股通/深股通)
  getNorthBound: (params) => apiClient.get('/market/north-bound', { params }),
  // 个股资金流向
  getStockFundFlow: (tsCode) => apiClient.get('/market/stock-fund-flow', { params: { ts_code: tsCode } }),
  // 个股资金流向排行 (全市场排名)
  getFundFlowRank: (params) => apiClient.get('/market/fund-flow-rank', { params }),
  // 行业资金流向 (同花顺THS)
  getMoneyflowInd: (params) => apiClient.get('/market/moneyflow-ind', { params }),
  // 行业板块列表 (用于自动补全联想)
  getIndustryList: () => apiClient.get('/market/industry-list'),
  // 大盘资金流向
  getMoneyflowMkt: (params) => apiClient.get('/market/moneyflow-mkt', { params }),
  // 沪深港通资金流向每日明细
  getMoneyflowHsgt: (params) => apiClient.get('/market/moneyflow-hsgt', { params }),
  // 融资融券汇总
  getMargin: (params) => apiClient.get('/market/margin', { params }),
  // 融资融券明细（个股级别）
  getMarginDetail: (params) => apiClient.get('/market/margin-detail', { params }),
  // 融资明细自定义股票管理
  getMarginCustomStocks: () => apiClient.get('/market/margin-custom-stocks'),
  addMarginCustomStock: (ts_code, name) => apiClient.post('/market/margin-custom-stocks', null, { params: { ts_code, name: name || '' } }),
  removeMarginCustomStock: (ts_code) => apiClient.delete(`/market/margin-custom-stocks/${ts_code}`),
  // 第一本账资金流向 (moneyflow_ths + 第一本账联动)
  getMoneyflowFirstBook: (params) => apiClient.get('/market/moneyflow-first-book', { params }),
  // 基金持仓查询 (Tushare fund_portfolio)
  getFundPortfolio: (params) => apiClient.get('/fund-portfolio/query', { params }),
  // 获取基金最新持仓报告期
  getFundPortfolioLatestPeriod: (tsCode) => apiClient.get('/fund-portfolio/latest-period', { params: { ts_code: tsCode } }),
  // 识别输入类型（基金代码/股票代码/基金公司）
  classifyFundInput: (keyword) => apiClient.get('/fund-portfolio/classify-input', { params: { keyword } }),
  // 公募基金基础信息列表 (Tushare fund_basic)
  getFundBasic: (params) => apiClient.get('/fund-portfolio/fund-basic', { params }),

  // ===== 新增: 板块与指数 =====
  // 指数成分权重
  getIndexWeight: (params) => apiClient.get('/market/index-weight', { params }),
  // 指数基本信息
  getIndexBasic: (params) => apiClient.get('/market/index-basic', { params }),
  // 板块成分股（正向/反向查询）
  getBoardMembers: (params) => apiClient.get('/market/board-members', { params }),
  // 板块分类列表（申万/同花顺）
  getBoardList: (params) => apiClient.get('/market/board-list', { params }),
  // 概念板块日线K线
  getBoardKline: (params) => apiClient.get('/market/board-kline', { params }),
  // 指数日线技术因子
  getIdxFactorPro: (params) => apiClient.get('/market/idx-factor-pro', { params })
}

export default {
  firstBook: firstBookApi,
  stock: stockApi,
  indicator: indicatorApi,
  indexIndicator: indexIndicatorApi,
  dataSource: dataSourceApi,
  notification: notificationApi,
  log: logApi,
  conclusion: conclusionApi,
  scheduledTask: scheduledTaskApi,
  dashboard: dashboardApi,
  chatAnalysis: chatAnalysisApi,
  documents: documentsApi,
  stkMins: stkMinsApi,
  rtIdxMin: rtIdxMinApi,
  idxMins: idxMinsApi,
  systemSettings: systemSettingsApi,
  marketData: marketDataApi
}
